import { ApiError } from '@echo/api-client/client';
import { claimInvitations, getGroupChat, resolveParticipants } from '@echo/api-client/onearly';

/**
 * Core's half of group chat (PRD-2334, core#770): which Reach thread is a group's chat, what to
 * call the people in it and who leads the group, and seating someone Reach didn't know when Core
 * last synced the group. Reach holds the messages (`ReachGroupChatTransport`).
 */

/** Where a group's chat lives; the person keeps their connection at home. */
export interface GroupChatBinding {
  reachHome: string;
  reachThreadId: string;
  reachSpaceId: string;
}

/**
 * Why Core has no chat to open: `not-found` (404 `GROUP_CHAT_NOT_FOUND`, Core's one answer for
 * not a current member, no such group, a group without chat and a region with chat off) or
 * `homed-elsewhere` (409 `HOMED_ELSEWHERE`: the group, and its chat, live in another region).
 */
export type GroupChatUnavailableReason = 'not-found' | 'homed-elsewhere';

/** Core has no chat for this person in this group. Trying again won't change that. */
export class GroupChatUnavailableError extends Error {
  constructor(
    readonly groupActorUuid: string,
    readonly reason: GroupChatUnavailableReason = 'not-found',
  ) {
    super('Group chat is not available for this group');
    this.name = 'GroupChatUnavailableError';
  }
}

/**
 * Whether a chat failure is an explicit refusal of access (no chat for this person, REST 403/404,
 * or Reach's stream refusing the thread) rather than a connection failure. Matches Reach's
 * `ApiError` (`status`) and `ThreadRpcError` (`code`, `reason`) by shape: this module can't import
 * the Reach SDK.
 */
export function isGroupChatAccessRefused(error: unknown): boolean {
  if (error instanceof GroupChatUnavailableError) return true;
  if (typeof error !== 'object' || error === null) return false;
  const { status, code, reason, action } = error as Record<string, unknown>;
  if (typeof status === 'number') return status === 403 || status === 404;
  if (typeof action !== 'string') return false;
  return code === 403 || code === 404 || reason === 'thread not found';
}

/** Core couldn't reach Reach just now (503 `GROUP_CHAT_UNAVAILABLE`): trying again may work. */
export class GroupChatRetryableError extends Error {
  readonly retryable = true;

  constructor(
    readonly groupActorUuid: string,
    options?: { cause?: unknown },
  ) {
    super('Group chat could not be opened just now; try again', options);
    this.name = 'GroupChatRetryableError';
  }
}

/**
 * `GET /api/v1/groups/{groupActorUuid}/chat`: the group's thread, to a current member. Core
 * creates a Stronger Memory group's thread the first time someone opens it.
 */
export async function resolveGroupChat(groupActorUuid: string): Promise<GroupChatBinding> {
  let response: Awaited<ReturnType<typeof getGroupChat>>;
  try {
    response = await getGroupChat(groupActorUuid);
  } catch (error) {
    // The api client throws `ApiError` for anything but a 2xx, with Core's status on it.
    if (error instanceof ApiError) {
      if (error.status === 404) throw new GroupChatUnavailableError(groupActorUuid, 'not-found');
      if (error.status === 409) {
        throw new GroupChatUnavailableError(groupActorUuid, 'homed-elsewhere');
      }
      if (error.status === 503) throw new GroupChatRetryableError(groupActorUuid, { cause: error });
    }
    throw error;
  }
  if (response.status !== 200) {
    throw new Error(`OnEarly: opening the group chat failed with status ${response.status}`);
  }
  const { reachThreadId, reachSpaceId, reachHome } = response.data;
  if (!/^[a-z]{2}$/.test(reachHome)) {
    throw new Error('OnEarly: the group chat response has no valid Reach home region');
  }
  return { reachThreadId, reachSpaceId, reachHome };
}

/** A person in a chat, as Core names them for the caller. */
export interface ChatParticipant {
  reachUserId: string;
  name: string;
  avatarUrl: string | undefined;
  /**
   * In a group's own chat, for a caller who is in the group: `admin` for a holder of the group's
   * Admin role (shown as Leader), `member` for anyone else in it. Undefined otherwise.
   */
  groupRole: 'admin' | 'member' | undefined;
}

/** Core takes at most this many ids per resolve call. */
const RESOLVE_BATCH = 100;

function batches<T>(items: readonly T[]): T[][] {
  const out: T[][] = [];
  for (let start = 0; start < items.length; start += RESOLVE_BATCH) {
    out.push(items.slice(start, start + RESOLVE_BATCH));
  }
  return out;
}

/**
 * `POST /api/v1/chat/participants/resolve`: names and pictures for Reach users, and, for the
 * thread `reachThreadId` names when it is a group's chat, each one's role in the group. Core
 * answers only for people the caller may see (anyone in a thread the caller is in); the rest are
 * absent.
 */
export async function resolveChatParticipants(
  reachUserIds: readonly string[],
  reachThreadId?: string,
): Promise<ChatParticipant[]> {
  const unique = [...new Set(reachUserIds)];
  const resolved: ChatParticipant[] = [];
  for (const batch of batches(unique)) {
    const response = await resolveParticipants({
      reachUserIds: batch,
      invitationIds: [],
      ...(reachThreadId ? { reachThreadId } : {}),
    });
    if (response.status !== 200) {
      throw new Error(`OnEarly: naming chat participants failed with status ${response.status}`);
    }
    for (const actor of response.data.actors) {
      resolved.push({
        reachUserId: actor.reachUserId,
        name: actor.name,
        avatarUrl: actor.avatarUrl ?? undefined,
        groupRole: actor.groupRole,
      });
    }
  }
  return resolved;
}

/**
 * `POST /api/v1/chat/invitations/claim`, the nudge: Core seats the caller in every thread it held a
 * place in for them, such as a group's chat synced before Reach knew them. Needs the person's Reach
 * user to exist, so call it after Reach has seen them (`auth/me`).
 */
export async function claimChatInvitations(): Promise<void> {
  const response = await claimInvitations();
  if (response.status !== 200) {
    throw new Error(`OnEarly: claiming chat places failed with status ${response.status}`);
  }
}
